/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 04.08.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";

import ViewController from "./ViewController";
import t from "../../../trans";
import Navigation from "../../../Navigation";
import {PutRestAction, PostRestAction} from "../../../services/RestAction";
import UiSchema from "../../../UiSchema";
import SchemaRegistry from "../../../services/SchemaRegistry";
import {deepClone} from "../../../util";
import AskPassword from "../../../modals/AskPassword";
import ParamParser from "../../../ParamParser";

class EditController extends ViewController {
    static propTypes = {
        basePath: PropTypes.string.isRequired,
        params: PropTypes.shape({
            path: PropTypes.string.isRequired,
            resource: PropTypes.string.isRequired,
            action: PropTypes.oneOf(["list","new","view","edit","delete"]).isRequired,
            id: PropTypes.string,
            query: PropTypes.object,
            tab: PropTypes.string,
            parentPath: PropTypes.string,
            parentResource: PropTypes.string,
            parentAction: PropTypes.oneOf(["list","new","view","edit","delete"]),
            parentId: PropTypes.string,
            parentField: PropTypes.string,
            parentTab: PropTypes.string,
            isModal: PropTypes.bool
        }).isRequired,
        schema: PropTypes.instanceOf(UiSchema).isRequired,
        location: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired,
        route: PropTypes.object.isRequired
    };

    static defaultProps = {};

    constructor(props, storeMap = null) {
        super(props, storeMap);
        this.state.isLeaving = false;
        this.routerWillLeave = this.routerWillLeave.bind(this);
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);
    }

    componentWillUnmount() {
        this.props.router.setRouteLeaveHook(this.props.route, null);
        return super.componentWillUnmount();
    }

    routerWillLeave(nextLocation) {
        if (this.state.isLeaving || nextLocation.pathname.startsWith(this.props.location.pathname + "/")) return true;
        if (this.state.resource.editRecordIsChanged) return t("You have unsaved modifications. Are you sure you want to leave?");
        return true;
    }

    loadResource() {
        super.loadResource();
        this.editCurrentRecord();
    }

    getActionData(a) {
        if (this.state.resource)
            return this.state.resource.editRecord;
        return null;
    }

    editCurrentRecord() {
        // if (this.isResourceInEditBuffer()) return;
        if (this.isResourceLoaded()) this.actions().editCurrentRecord.defer();
        else this.actions().clearEditRecord.defer();
    }

    isResourceInEditBuffer() {
        return (this.state.resource &&
            this.state.resource.editRecord &&
            this.state.resource.editRecord.id == this.props.params.id);
    }

    loadingFinished(key, newState, oldState) {
        if (key === "resource") {
            switch (newState.action.method) {
                case "get":
                    // Copy loaded result to edit buffer.
                    this.actions().editCurrentRecord.defer();
                    break;
                case "put":
                    // Data is saved, go back:
                    this.actions().clearEditRecordChangedFlag.defer();
                    this.setState({isLeaving: true}, function () {
                        this.navigateBack();
                    }.bind(this));
                    break;
            }
        }
    }

    loadingFailed(key, newState) {
        if (key === "resource" && newState.action.error && newState.action.error.reason) {
            switch (newState.action.error.reason) {
                case "auth:reauthRequired":
                    // Reauthentication required. Reauthenticate and save again:
                    this.openModal(AskPassword);
                    return;
            }
        }
        return super.loadingFailed(key, newState);
    }


    onCloseModal(success) {
        super.onCloseModal(success);
        if (success) {
            this.saveResource();
        }
    }

    /**
     * Called after user has saved data. Method navigates back to previous page.
     */
    navigateBack() {
        Navigation.gotoBackOrUp();
    }

    /**
     *
     * @param {PageAction} action
     * @returns {boolean}
     */
    onAction(action) {
        switch (action.action) {
            case "back":
                this.setState({isLeaving: true}, function () {
                    //this.actions().clearEditRecord();
                    this.navigateBack();
                }.bind(this));
                return true;
            case "save":
                this.saveResource();
                return true;
        }
        return super.onAction(action);
    }

    saveResource() {

        // Transform empty field values to ""/0/false:
        let data = this.fixEmptyValues(this.state.resource.editRecord);
        data = this.applyFilter(data);
        // Save data:
        if (this.props.params.id)
            this.actions().actionStart.defer(new PutRestAction(this.props.params.id, data));
        else
            this.actions().actionStart.defer(new PostRestAction(data));
    }

    applyFilter(data) {
        let schema = this.getEditSchema();
        let filter = this.calcFilter(schema.getUiSchemaObject().getFilter());
        for (let k of _.keys(filter)) {
            let kItems = k.split("/");
            let c = data;
            for (let i = 0; i < kItems.length-1; i++) {
                if (!c[kItems[i]]) c[kItems[i]] = {};
                c = c[kItems[i]];
            }
            c[kItems[kItems.length-1]] = filter[k];
        }
        return data;
    }

    /**
     * Calculate filter parameters
     * @param {Object} filter
     * @returns {Object}
     */
    calcFilter(filter) {
        if (!filter) return null;
        let paramParser = new ParamParser(this.props.params, this.state.locationInfo.getLocationProps());
        return paramParser.replaceValues(filter);
        /*
        return _.mapObject(filter, function (value, key) {
            return paramParser.replaceValues()
            return this.evalExpression(value);
        }.bind(this));*/
    }


    fixEmptyValues(data) {
        let schema = this.getEditSchema();
        let fields = schema.getOrder();
        let newData = deepClone(data);
        this.fixEmptyValuesObject(newData, schema, fields);
        return newData;
    }

    fixEmptyValuesObject(data, schema, fields) {
        if (!fields) return;
        for (let k of fields) {
            let currentData = data;
            let currentSchema = schema;

            let splitted = k.split("/");
            let field = splitted.pop();
            for (let s of splitted) {
                currentSchema = currentSchema.field(s);
                switch (currentSchema.getType()) {
                    case "object":
                        if (!currentData[s]) currentData[s] = {};
                        break;
                    case "array":
                        if (!currentData[s]) currentData[s] = [];
                        break;
                    default:
                        break;
                }
                currentData = currentData[s];
            }
            currentSchema = currentSchema.field(field);
            if (currentData[field] == null) {
                switch (currentSchema.getType()) {
                    case "array":
                        currentData[field] = [];
                        break;
                    case "object":
                        currentData[field] = {};
                        break;
                    case "integer":
                        currentData[field] = 0;
                        break;
                    case "boolean":
                        currentData[field] = false;
                        break;
                    case "string":
                        switch (currentSchema.getFormat()) {
                            case "date-time":
                            case "date":
                            case "time":
                                // Server JSON parser does not accept empty string as time value.
                                // We should use null instead of this.
                                break;
                        }
                    default:
                        currentData[field] = "";
                        break;
                }
            } else {
                // Traverse into array:
                switch (currentSchema.getType()) {
                    case "array":
                        currentSchema = currentSchema.field("*");
                        let order = currentSchema.getOrder();
                        if (order) {
                            for (let r of currentData[field]) {
                                this.fixEmptyValuesObject(r, currentSchema, order);
                            }
                        }
                        break;
                    case "object":
                        break;
                }
            }
        }
    }

    /**
     *
     * @returns {Schema}
     */
    getEditSchema() {
        let uiSchema = this.getUiSchema();
        let options = uiSchema.getOptions();
        let schema = options.schema;
        // return SchemaRegistry.getSchemaObject(this.props.params.parentResource, this.props.params.resource, schema);
        return SchemaRegistry.getSchemaObject(this.state.locationInfo.getNavResource(), this.props.params.resource, schema);
    }


    onActionFinished(action, restAction, failed) {
        if (!failed) {
            switch (action.action) {
                case "save":
                    // Go back:
                    this.navigateBack();
                    return true;
            }
        }
        return super.onActionFinished(action, restAction, failed);
    }

    /**
     * Called when action returns client-side errors
     * @param {Object} errorInfo
     * @param {RestAction} action
     */
    onError(errorInfo, action) {
        if (action.action !== "save") return false;
        this.actions().setEditErrors(errorInfo);
        return true;
    }

}

export default EditController;
